/**
 * Unified API Service - One File Per Folder Pattern
 * Consolidates all API and service architecture into a single, consistent system
 * 
 * This service integrates:
 * - ApiClient.js (centralized HTTP client)
 * - use-http.js (legacy HTTP hook)
 * - DataServices.js (entity-specific services)
 * - apiEndpoints.js (endpoint constants)
 * 
 * Following the component-first philosophy established in Base components
 */

import { apiClient } from './ApiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { appStore } from '../store/AppStore';

/**
 * Base Service Class
 * Provides standardized CRUD operations for all entities
 */
class BaseEntityService {
  constructor(entityName, endpoints = {}) {
    this.entityName = entityName;
    this.endpoints = {
      list: endpoints.list || `/v1/${entityName}/GetAll`,
      create: endpoints.create || `/v1/${entityName}/Create`,
      update: endpoints.update || `/v1/${entityName}/Update`,
      delete: endpoints.delete || `/v1/${entityName}/Delete`,
      details: endpoints.details || `/v1/${entityName}/GetDetails`,
      ...endpoints
    };
    
    // Bind methods to preserve context
    this.fetchAll = this.fetchAll.bind(this);
    this.fetchById = this.fetchById.bind(this);
    this.create = this.create.bind(this);
    this.update = this.update.bind(this);
    this.delete = this.delete.bind(this);
    this.customCall = this.customCall.bind(this);
  }

  /**
   * Fetch all entities with optional filters
   */
  async fetchAll(filters = {}, options = {}) {
    try {
      const response = await apiClient.post(this.endpoints.list, {
        emailID: this.getCurrentUser(),
        roleID: this.getCurrentRole(),
        corporateID: this.getCurrentCorporateId(),
        ...filters
      }, options);

      // Update store if enabled
      if (options.updateStore !== false) {
        appStore.setEntityData(this.entityName, response);
      }

      return response;
    } catch (error) {
      // Enhanced error handling for 404 and other common API errors
      if (error.message && error.message.includes('404')) {
        console.warn(`⚠️ ${this.entityName} API endpoint not available (404): ${this.endpoints.list}`);
        console.log(`💡 Returning empty ${this.entityName} list until API endpoint is available`);
        return { [this.entityName + 'List']: [] }; // Return empty list with expected structure
      }
      console.error(`Error fetching ${this.entityName}:`, error);
      throw error;
    }
  }

  /**
   * Fetch entity by ID
   */
  async fetchById(id, options = {}) {
    try {
      const response = await apiClient.post(this.endpoints.details, {
        emailID: this.getCurrentUser(),
        roleID: this.getCurrentRole(),
        corporateID: this.getCurrentCorporateId(),
        id,
        ...options.filters
      }, options);

      return response;
    } catch (error) {
      console.error(`Error fetching ${this.entityName} by ID:`, error);
      throw error;
    }
  }

  /**
   * Create new entity
   */
  async create(data, options = {}) {
    try {
      const response = await apiClient.post(this.endpoints.create, {
        emailID: this.getCurrentUser(),
        roleID: this.getCurrentRole(),
        corporateID: this.getCurrentCorporateId(),
        ...data
      }, options);

      // Refresh store data if successful
      if (response.Message?.toLowerCase() === 'success' && options.refreshStore !== false) {
        await this.fetchAll({}, { updateStore: true });
      }

      return response;
    } catch (error) {
      console.error(`Error creating ${this.entityName}:`, error);
      throw error;
    }
  }

  /**
   * Update existing entity
   */
  async update(id, data, options = {}) {
    try {
      const response = await apiClient.post(this.endpoints.update, {
        emailID: this.getCurrentUser(),
        roleID: this.getCurrentRole(),
        corporateID: this.getCurrentCorporateId(),
        id,
        ...data
      }, options);

      // Refresh store data if successful
      if (response.Message?.toLowerCase() === 'success' && options.refreshStore !== false) {
        await this.fetchAll({}, { updateStore: true });
      }

      return response;
    } catch (error) {
      console.error(`Error updating ${this.entityName}:`, error);
      throw error;
    }
  }

  /**
   * Delete entity
   */
  async delete(id, options = {}) {
    try {
      const response = await apiClient.post(this.endpoints.delete, {
        emailID: this.getCurrentUser(),
        roleID: this.getCurrentRole(),
        corporateID: this.getCurrentCorporateId(),
        id
      }, options);

      // Refresh store data if successful
      if (response.Message?.toLowerCase() === 'success' && options.refreshStore !== false) {
        await this.fetchAll({}, { updateStore: true });
      }

      return response;
    } catch (error) {
      console.error(`Error deleting ${this.entityName}:`, error);
      throw error;
    }
  }

  /**
   * Custom API call for entity-specific operations
   */
  async customCall(endpoint, data = {}, options = {}) {
    const method = options.method || 'POST';
    const fullEndpoint = this.endpoints[endpoint] || endpoint;

    try {
      const requestData = {
        emailID: this.getCurrentUser(),
        roleID: this.getCurrentRole(),
        corporateID: this.getCurrentCorporateId(),
        ...data
      };

      let response;
      switch (method.toUpperCase()) {
        case 'GET':
          response = await apiClient.get(fullEndpoint, { params: requestData, ...options });
          break;
        case 'POST':
          response = await apiClient.post(fullEndpoint, requestData, options);
          break;
        case 'PUT':
          response = await apiClient.put(fullEndpoint, requestData, options);
          break;
        case 'DELETE':
          response = await apiClient.delete(fullEndpoint, { ...options, body: requestData });
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      return response;
    } catch (error) {
      // Enhanced error handling for 404 and other common API errors
      if (error.message && error.message.includes('404')) {
        console.warn(`⚠️ ${this.entityName} custom API endpoint not available (404): ${fullEndpoint}`);
        console.log(`💡 Custom call for ${endpoint} failed, endpoint may not be implemented`);
        // Return empty response structure for 404 errors
        return { Message: 'API endpoint not available', Data: null };
      }
      console.error(`Error in custom call for ${this.entityName}:`, error);
      throw error;
    }
  }

  // Helper methods for authentication context
  getCurrentUser() {
    return localStorage.getItem('user') || '';
  }

  getCurrentRole() {
    return localStorage.getItem('roleId') || '';
  }

  getCurrentCorporateId() {
    const roleId = this.getCurrentRole();
    return roleId === '2' ? localStorage.getItem('corpId') || '' : '';
  }
}

/**
 * Specialized Services
 * Each service extends BaseEntityService with entity-specific configurations
 */

// Corporate Service
export const corporateService = new BaseEntityService('Corporate', {
  list: API_ENDPOINTS.CORPORATE.GET_ALL,
  GET_ALL: API_ENDPOINTS.CORPORATE.GET_ALL, // Add explicit mapping for backward compatibility
  GET_CORPORATES: API_ENDPOINTS.CORPORATE.GET_ALL, // Alternative mapping
  create: API_ENDPOINTS.CORPORATE.ADD_REGISTRATION,
  update: API_ENDPOINTS.CORPORATE.ADD_EDIT_PARAMETER,
  departments: API_ENDPOINTS.CORPORATE.GET_DEPARTMENTS,
  GET_DEPARTMENTS: API_ENDPOINTS.CORPORATE.GET_DEPARTMENTS, // Add explicit mapping for CorporateFilter component
  staffList: API_ENDPOINTS.CORPORATE.STAFF_LIST_BY_CORPORATE,
  STAFF_LIST_BY_CORPORATE: API_ENDPOINTS.CORPORATE.STAFF_LIST_BY_CORPORATE, // Explicit mapping for TestStopInfo component
  shuttleStops: API_ENDPOINTS.CORPORATE.GET_SHUTTLE_STOP,
  GET_SHUTTLE_STOP: API_ENDPOINTS.CORPORATE.GET_SHUTTLE_STOP, // Explicit mapping so customCall('GET_SHUTTLE_STOP') works
  addEditShuttleStop: API_ENDPOINTS.CORPORATE.ADD_EDIT_SHUTTLE_STOP,
  
  // Corporate registration endpoints
  ADD_CORPORATE_REG_V2: API_ENDPOINTS.CORPORATE_EXTENDED.ADD_CORPORATE_REG_V2, // Use the same endpoint as legacy version
  CORPORATE_REGISTRATION: API_ENDPOINTS.CORPORATE_EXTENDED.CORPORATE_REGISTRATION, // Primary registration endpoint
});

// Staff Service
export const staffService = new BaseEntityService('Staff', {
  list: API_ENDPOINTS.STAFF.GET_STAFF_LIST,
  GET_STAFF_LIST: API_ENDPOINTS.STAFF.GET_STAFF_LIST, // Add explicit mapping for backward compatibility
  STAFF_LIST_BY_CORPORATE: API_ENDPOINTS.CORPORATE.STAFF_LIST_BY_CORPORATE, // Add corporate staff list endpoint
  create: API_ENDPOINTS.STAFF.ADD_EDIT_STAFF_V2,
  update: API_ENDPOINTS.STAFF.ADD_EDIT_STAFF_V2,
  details: API_ENDPOINTS.STAFF.GET_DETAILS,
  GET_DETAILS: API_ENDPOINTS.STAFF.GET_DETAILS,
  littleStaff: API_ENDPOINTS.STAFF.ADD_EDIT_LITTLE_STAFF,
  ADD_EDIT_LITTLE_STAFF: API_ENDPOINTS.STAFF.ADD_EDIT_LITTLE_STAFF,
  transfer: API_ENDPOINTS.STAFF.TRANSFER_STAFF,
  TRANSFER_STAFF: API_ENDPOINTS.STAFF.TRANSFER_STAFF,
  suspend: API_ENDPOINTS.STAFF.SUSPEND_STAFF,
  SUSPEND_STAFF: API_ENDPOINTS.STAFF.SUSPEND_STAFF,
});

// Driver Service - Aligned with Postman collection endpoints
export const driverService = new BaseEntityService('Driver', {
  list: API_ENDPOINTS.DRIVER.GET_LIST, // Uses /v1/DriverList/GetDriverList from Postman
  privateList: API_ENDPOINTS.DRIVER.GET_PRIVATE_LIST,
  shuttleList: API_ENDPOINTS.DRIVER.GET_SHUTTLE_LIST,
  allWithRiders: API_ENDPOINTS.DRIVER.GET_ALL_WITH_RIDERS,
  details: API_ENDPOINTS.DRIVER.GET_DETAILS,
  create: API_ENDPOINTS.DRIVER.ADD_EDIT_PRIVATE,
  update: API_ENDPOINTS.DRIVER.ADD_EDIT_PRIVATE,
  onlineLocations: API_ENDPOINTS.DRIVER.ONLINE_LOCATIONS,
  ONLINE_LOCATIONS: API_ENDPOINTS.DRIVER.ONLINE_LOCATIONS, // Add explicit mapping for backward compatibility
  // Note: getLocation removed - not in Postman (use ONLINE_LOCATIONS instead)
  partnersWithVehicles: API_ENDPOINTS.DRIVER.GET_PARTNERS_WITH_VEHICLES,
  assignVehicle: API_ENDPOINTS.DRIVER.ASSIGN_VEHICLE,
  // Explicit endpoint mappings
  DRIVER_LIST_V2: API_ENDPOINTS.DRIVER.DRIVER_LIST_V2, // Uses /v1/DriverList/DriverListV2 from Postman
  GET_DRIVER_LIST: API_ENDPOINTS.DRIVER.GET_LIST, // Uses /v1/DriverList/GetDriverList from Postman
  GET_SHUTTLE_DRIVER_LIST: API_ENDPOINTS.DRIVER.GET_SHUTTLE_LIST, // Explicit shuttle mapping
  GET_PRIVATE_DRIVER_LIST: API_ENDPOINTS.DRIVER.GET_PRIVATE_LIST, // Add explicit mapping for Modal component
  GET_ALL_WITH_RIDERS: API_ENDPOINTS.DRIVER.GET_ALL_WITH_RIDERS, // Add explicit mapping for EditDriver component
});

// Trip Service
export const tripService = new BaseEntityService('Trip', {
  list: API_ENDPOINTS.TRIPS.GET_ALL,
  details: API_ENDPOINTS.TRIPS.GET_DETAILS,
  shuttleTrips: API_ENDPOINTS.TRIPS.GET_SHUTTLE,
  allTripsDetails: API_ENDPOINTS.TRIPS.ALL_TRIPS_DETAILS,
  shuttleTripsDetails: API_ENDPOINTS.TRIPS.SHUTTLE_TRIPS_DETAILS,
  liveDetails: API_ENDPOINTS.LIVE_TRIP.GET_DETAILS,
  GET_SHUTTLE: API_ENDPOINTS.TRIPS.GET_SHUTTLE, // Add explicit mapping for Trips component
  GET_ALL_TRIPS: API_ENDPOINTS.TRIPS.GET_ALL, // Add explicit mapping for CommonTrips component
});

// Route Service - Aligned with Postman collection (uses singular 'Route')
export const routeService = new BaseEntityService('Route', {
  list: API_ENDPOINTS.ROUTES.GET_LIST, // Use GetRoutList from Postman (not GetAllRoutes which doesn't exist)
  create: API_ENDPOINTS.ROUTE.ADD_EDIT,
  update: API_ENDPOINTS.ROUTE.ADD_EDIT,
  // Note: delete endpoint removed - not in Postman collection
  details: API_ENDPOINTS.ROUTE.GET_DETAILS,
  routeList: API_ENDPOINTS.ROUTE.GET_LIST,
  GET_LIST: API_ENDPOINTS.ROUTE.GET_LIST, // Add explicit mapping for backward compatibility
  GET_ROUTE_LIST: API_ENDPOINTS.ROUTE.GET_LIST, // Alternative mapping for components using GET_ROUTE_LIST
  ADD_EDIT: API_ENDPOINTS.ROUTE.ADD_EDIT, // CRITICAL FIX: Add explicit mapping for TestStopInfo component
  bookedRoute: API_ENDPOINTS.ROUTES.GET_BOOKED_ROUTE,
  enableDisableShuttle: API_ENDPOINTS.ROUTES.ENABLE_DISABLE_SHUTTLE,
  ENABLE_DISABLE_SHUTTLE_ROUTE: API_ENDPOINTS.ROUTES.ENABLE_DISABLE_SHUTTLE,
  
  // Corporate endpoints needed for shuttle stops
  GET_CORPORATES: API_ENDPOINTS.CORPORATE.GET_ALL, // Add corporate list for shuttle stops
  GET_SHUTTLE_STOP: API_ENDPOINTS.CORPORATE.GET_SHUTTLE_STOP, // Add shuttle stop endpoint
  ADD_EDIT_SHUTTLE_STOP: API_ENDPOINTS.CORPORATE.ADD_EDIT_SHUTTLE_STOP, // Add shuttle stop edit endpoint
});

// Booking Service
export const bookingService = new BaseEntityService('Booking', {
  // Schedule Booking
  scheduleTrips: API_ENDPOINTS.SCHEDULE.GET_TRIPS,
  scheduleTripDetails: API_ENDPOINTS.SCHEDULE.TRIP_DETAILS,
  addEditBooking: API_ENDPOINTS.SCHEDULE.ADD_EDIT_BOOKING,
  bookingRequestDetails: API_ENDPOINTS.SCHEDULE_BOOKING.GET_BOOKING_REQUEST_DETAILS,
  GET_BOOKING_REQUEST_DETAILS: API_ENDPOINTS.SCHEDULE_BOOKING.GET_BOOKING_REQUEST_DETAILS, // Add explicit mapping
  cancelScheduleTrip: API_ENDPOINTS.SCHEDULE_BOOKING.CANCEL_SCHEDULE_TRIP,

  // Shuttle Booking
  editRoute: API_ENDPOINTS.SHUTTLE_BOOKING.EDIT_ROUTE,
  EDIT_ROUTE_BOOKING: API_ENDPOINTS.SHUTTLE_BOOKING.EDIT_ROUTE, // Add explicit mapping for DataTable component
  cancelRoute: API_ENDPOINTS.SHUTTLE_BOOKING.CANCEL_ROUTE,
  bookedRiderDetails: API_ENDPOINTS.SHUTTLE_BOOKING_EXTENDED.GET_BOOKED_RIDER_DETAILS,

  // Additional endpoints for shuttle bookings
  GET_SHUTTLE_DRIVER_LIST: API_ENDPOINTS.SHUTTLE_TRIPS.GET_SHUTTLE_DRIVER_LIST,
  GET_BOOKED_ROUTE: API_ENDPOINTS.ROUTES.GET_BOOKED_ROUTE,
  
  // Route endpoints needed for shuttle booking form
  GET_ROUTE_LIST: API_ENDPOINTS.ROUTE.GET_LIST, // Add route list endpoint for shuttle booking forms
  ADD_EDIT_DRIVER_WITH_RIDER_ASSIGN: API_ENDPOINTS.SHUTTLE_TRIPS.ADD_EDIT_DRIVER_WITH_RIDER,
  
  // Corporate endpoints needed for schedule booking form
  GET_CORPORATES: API_ENDPOINTS.CORPORATE.GET_ALL, // Add corporate list for schedule booking
  GET_DEPARTMENTS: API_ENDPOINTS.CORPORATE.GET_DEPARTMENTS, // Add departments for schedule booking
});

// Driver Shift Service
export const driverShiftService = new BaseEntityService('DriverShift', {
  list: API_ENDPOINTS.DRIVER_SHIFT.GET_DRIVER_SHIFT_DETAILS,
  create: API_ENDPOINTS.DRIVER_SHIFT.ADD_EDIT_DRIVER_SHIFT,
  update: API_ENDPOINTS.DRIVER_SHIFT.ADD_EDIT_DRIVER_SHIFT,
  
  // Explicit mappings for component compatibility
  GET_DRIVER_SHIFT_DETAILS: API_ENDPOINTS.DRIVER_SHIFT.GET_DRIVER_SHIFT_DETAILS,
  ADD_EDIT_DRIVER_SHIFT: API_ENDPOINTS.DRIVER_SHIFT.ADD_EDIT_DRIVER_SHIFT,
});

// Authentication Service
export const authService = new BaseEntityService('Auth', {
  login: API_ENDPOINTS.AUTH.LOGIN,
  logout: API_ENDPOINTS.AUTH.LOGOUT,
  forgotPassword: API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
  resetPassword: API_ENDPOINTS.AUTH.RESET_PASSWORD,
  authenticateOTP: API_ENDPOINTS.AUTH.AUTHENTICATE_OTP,
  authenticateUser: API_ENDPOINTS.AUTHENTICATION.AUTHENTICATE_USER,
  setNewPassword: API_ENDPOINTS.AUTHENTICATION.SET_NEW_PASSWORD,
});

// Department Service - All endpoints from Postman collection
export const departmentService = new BaseEntityService('Department', {
  list: API_ENDPOINTS.DEPARTMENT.GET_LIST,
  DEPARTMENT_LIST: API_ENDPOINTS.DEPARTMENT.GET_LIST, // Add explicit mapping for backward compatibility
  details: API_ENDPOINTS.DEPARTMENT.GET_DETAILS,
  GET_DEPARTMENT_DETAILS: API_ENDPOINTS.DEPARTMENT.GET_DETAILS, // Explicit mapping for AddDepartment component
  create: API_ENDPOINTS.DEPARTMENT.ADD_EDIT,
  update: API_ENDPOINTS.DEPARTMENT.ADD_EDIT,
  ADD_EDIT_DEPARTMENT: API_ENDPOINTS.DEPARTMENT.ADD_EDIT, // Explicit mapping for AddDepartment component
});

// Settings Service
export const settingsService = new BaseEntityService('Settings', {
  getShiftTask: API_ENDPOINTS.SETTINGS.GET_SHIFT_TASK,
  addEditShiftTask: API_ENDPOINTS.SETTINGS.ADD_EDIT_SHIFT_TASK,
  changePassword: API_ENDPOINTS.HEADER.CHANGE_PASSWORD,
  getNotification: API_ENDPOINTS.HEADER.GET_NOTIFICATION,
  getUserMenuList: API_ENDPOINTS.MENU.GET_USER_LIST,
  GET_USER_MENU_LIST: API_ENDPOINTS.MENU.GET_USER_LIST, // Add explicit mapping for SideMenu component
  // Dashboard endpoints
  GET_DASHBOARD: API_ENDPOINTS.DASHBOARD.GET_DASHBOARD,
});

/**
 * Unified API Service
 * Main service that provides access to all entity services and common operations
 */
export class UnifiedApiService {
  constructor() {
    this.corporate = corporateService;
    this.staff = staffService;
    this.driver = driverService;
    this.trip = tripService;
    this.route = routeService;
    this.booking = bookingService;
    this.drivershift = driverShiftService; // Use lowercase to match getService method
    this.auth = authService;
    this.department = departmentService;
    this.settings = settingsService;
  }

  /**
   * Get service by entity name
   */
  getService(entityName) {
    const serviceName = entityName.toLowerCase();
    if (this[serviceName]) {
      return this[serviceName];
    }
    throw new Error(`Service not found for entity: ${entityName}`);
  }

  /**
   * Direct API call (for legacy compatibility)
   */
  async call(endpoint, data = {}, options = {}) {
    const method = options.method || 'POST';
    
    const requestData = {
      emailID: localStorage.getItem('user') || '',
      roleID: localStorage.getItem('roleId') || '',
      corporateID: localStorage.getItem('roleId') === '2' ? localStorage.getItem('corpId') || '' : '',
      ...data
    };

    switch (method.toUpperCase()) {
      case 'GET':
        return await apiClient.get(endpoint, { params: requestData, ...options });
      case 'POST':
        return await apiClient.post(endpoint, requestData, options);
      case 'PUT':
        return await apiClient.put(endpoint, requestData, options);
      case 'DELETE':
        return await apiClient.delete(endpoint, { ...options, body: requestData });
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  /**
   * Custom API call (alias for call method for consistency with BaseEntityService)
   */
  async customCall(endpoint, data = {}, options = {}) {
    return await this.call(endpoint, data, options);
  }
}

// Export singleton instance
export const unifiedApiService = new UnifiedApiService();

// Default export
export default unifiedApiService;
